import type { MerchantAccount, RawMerchantAccount } from '@core/models';

/**
 * Coerce one raw backend row into a fully-populated `MerchantAccount`.
 *
 * `String(value ?? '')` rather than a plain `?? ''` so a backend that serialises an id as a
 * number still yields something `.toLowerCase()` can be called on.
 */
function normalizeMerchantAccount(raw: RawMerchantAccount): MerchantAccount {
  return {
    // `account_id` is the only id present in BOTH response shapes, and the only one the
    // backend accepts in an `account_id` request field.
    accountId: String(raw.account_id ?? ''),
    // The success branch calls the merchant id `_id`; the pending branch calls it
    // `merchant_id`. Same value, two names — this is where they converge.
    merchantId: String(raw.merchant_id ?? raw._id ?? ''),
    merchantUserId: String(raw.merchant_user_id ?? ''),
    name: String(raw.name ?? ''),
    image: String(raw.image ?? ''),
    logo: String(raw.logo ?? ''),
    address: String(raw.address ?? ''),
    email: String(raw.email ?? ''),
    countryId: String(raw.country_id ?? ''),
    cityId: String(raw.city_id ?? ''),
    role: String(raw.role ?? ''),
    isDefaultMerchant: raw.is_default_merchant === true,
    migrateToMobileMoney: raw.migrate_to_mobile_money === true,
  };
}

/**
 * The single boundary between raw backend account rows and the rest of the app.
 *
 * Two jobs:
 *
 * 1. **Reconcile the two response shapes.** `/auth/login` returns the merchant id as `_id`
 *    and omits `merchant_user_id`; the multi-merchant branch returns `merchant_id` plus
 *    `merchant_user_id` and no `_id` at all. Downstream code should never have to know
 *    which branch produced a list.
 *
 * 2. **Make the declared contract true.** `MerchantAccount` marks every field required, but
 *    it is only a compile-time cast over `http.post<T>()` and over `JSON.parse` of
 *    localStorage. A row missing `email` or `name` otherwise reaches consumers as
 *    `undefined` and crashes the first `.toLowerCase()` that touches it — Sentry
 *    GOZEM-WEB-APP-1H, thrown from the account-switcher's `filteredAccounts` computed.
 *
 * Apply it in every store that accepts accounts from outside, so downstream code can trust
 * the type.
 *
 * Rows without an `accountId` are dropped rather than passed through: it is the `@for`
 * track key, and two blank keys throw an Angular duplicate-key error — a worse failure than
 * omitting an unusable account. (This filter previously keyed on `_id`, which the
 * multi-merchant payload does not contain at all, so it silently discarded every account
 * and left the picker empty.)
 *
 * @param accounts - Raw rows from the API, from localStorage, or from another store
 * @returns A new array of fully-populated accounts; never `null`
 */
export function normalizeMerchantAccounts(
  accounts: readonly RawMerchantAccount[] | null | undefined,
): MerchantAccount[] {
  if (!Array.isArray(accounts)) return [];

  return accounts
    .filter((raw): raw is RawMerchantAccount => typeof raw === 'object' && raw !== null)
    .map(normalizeMerchantAccount)
    .filter((account) => account.accountId !== '');
}
